/**
 * PURE. Body recipes (creatures §B7.3): neck, pear-sack torso with the belly bulge on the `belly` bone, arms + paws
 * (mitten / clawed / human fingers / hooves / webbed / talons; wing-arms with feather strips), legs (plantigrade and
 * digitigrade) + feet, the tail on its chain (S/J rest curve from the rig), the snail shell (log spiral, a disc in the
 * side plane) and the snail foot-skirt. Raw units.
 */
import type { BoneName } from '../../shared';
import { TORSO_RINGS } from '../rig/skeleton';
import { MAT, REG, part, type BuildCtx } from './ctx';
import { add, blend, cross, fan, lerp3, loft, nrm, resample, rigid, scl, segSkin, strip, sub, type Ring, type Skin, type V3 } from './prims';
import type { Surface } from './builder';

const TAU = Math.PI * 2;

/** Skin along a bone chain by height (torso): returns the 2-bone blend for y. */
function chainSkinY(ctx: BuildCtx, names: BoneName[], y: number): Skin {
  const ys = names.map((n) => ctx.P[n]![1]);
  if (y <= ys[0]) return rigid(ctx.bi(names[0]));
  for (let i = 0; i + 1 < names.length; i++) {
    if (y <= ys[i + 1]) {
      const t = (y - ys[i]) / Math.max(1e-6, ys[i + 1] - ys[i]);
      // blend only within ±20 % around the upper joint
      const k = Math.max(0, Math.min(1, (t - 0.7) / 0.6));
      return blend(ctx.bi(names[i]), ctx.bi(names[i + 1]), k * k * (3 - 2 * k));
    }
  }
  return rigid(ctx.bi(names[names.length - 1]));
}

function neck(ctx: BuildCtx): void {
  const { d, spec } = ctx;
  const nb = ctx.bi('neck'), hb = ctx.bi('head'), sb = ctx.bi('spine_02');
  const pn = ctx.P.neck!, ph = ctx.P.head!;
  const goose = spec.head.id === 'goose' || spec.body.id === 'goose';
  const slug = spec.flags.slugBody;
  const r0 = slug ? 0.062 * d.chestW : d.neckR * (goose ? 0.75 : 1);
  part(ctx, 'body.neck', (s) => {
    const y0 = pn[1] - 0.045 * d.kT, y1 = ph[1] + 0.035;
    const n = goose ? 5 : 3;
    const rings: Ring[] = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const y = y0 + (y1 - y0) * t;
      const z = pn[2] + (ph[2] - pn[2]) * t + (goose ? -0.035 * Math.sin(t * Math.PI) : 0);
      const r = slug ? r0 * (1 - 0.1 * t) : r0 * (1 + (goose ? -0.15 * t : 0.05 * (1 - t)));
      rings.push({ c: [0, y, z], rs: r, rf: r * (slug ? 0.85 : 0.92), skin: y < pn[1] ? blend(sb, nb, (y - y0) / Math.max(1e-6, pn[1] - y0)) : t > 0.75 ? blend(nb, hb, (t - 0.75) / 0.25) : rigid(nb) });
    }
    loft(s, rings, { sides: ctx.cfg.neckSides, front: [0, 0, 1], mat: ctx.bodyMat, region: REG.neck, jitter: 0.02, seed: ctx.seed + 21 });
  });
}

const BELLY_K: Record<string, number> = { flat: 0.15, tummy: 1, beer: 1.45, pouch: 1, abs: 0.55, ruff: 0.6 };

/** The torso loft ring at parameter t ∈ [0, 1] (hip → neck base): centre height, radius, belly push and skin.
 *  Garments shrink-wrap the same rings (creatures §B7.4). */
export function torsoRingAt(ctx: BuildCtx, t: number): { y: number; r: number; bellyAmt: number; hump: number; skin: Skin } {
  const { spec, d } = ctx;
  const o = spec.o;
  const radii = TORSO_RINGS[o.torso] ?? TORSO_RINGS.pear;
  const pelvisY = ctx.P.pelvis![1], neckY = ctx.P.neck![1];
  const slug = spec.flags.slugBody;
  const yb = pelvisY - 0.065 * d.kT, yt = neckY + 0.006;
  const n = radii.length;
  const f = Math.max(0, Math.min(n - 1, t * (n - 1)));
  const i = Math.min(n - 2, Math.floor(f)), w = f - i;
  const ringR = (k: number): number => {
    const tk = k / (n - 1);
    const wMul = k <= 2 ? d.hipW * (1 - tk) + d.chestW * tk : d.chestW;
    let r = radii[k] * wMul * (slug ? 0.85 : 1);
    if (slug) r = Math.max(r, 0.06 * d.chestW);
    return r;
  };
  const r = ringR(i) * (1 - w) + ringR(i + 1) * w;
  const bk = (BELLY_K[o.belly] ?? 1) * (0.25 + 0.75 * d.belly);
  const bw = [0, o.belly === 'pouch' ? 0.85 : 0.55, 0.75, 0.35, 0, 0, 0, 0];
  const bellyAmt = bk * r * ((bw[i] ?? 0) * (1 - w) + (bw[i + 1] ?? 0) * w);
  const hump = o.torso === 'hunched' || o.posture === 'hunchback' ? 0.03 * Math.sin(t * Math.PI) : 0;
  const y = yb + (yt - yb) * t;
  return { y, r, bellyAmt, hump, skin: chainSkinY(ctx, ['pelvis', 'spine_01', 'spine_02', 'neck'], y) };
}

/** Belly / hump deformation of a torso-shaped ring point (θ in turns, 0.25 = front). */
export function torsoDeform(p: V3, th: number, bellyAmt: number, hump: number, abs = false): V3 {
  const s2 = Math.sin(th * TAU);
  let z = p[2];
  if (s2 > 0 && bellyAmt > 0) z += bellyAmt * Math.pow(s2, 2.2) * (abs ? 1 + 0.25 * Math.sign(Math.sin(th * TAU * 3)) : 1);
  if (s2 < 0 && hump) z -= hump * Math.pow(-s2, 1.5);
  return [p[0], p[1], z];
}

function torso(ctx: BuildCtx): void {
  const { spec, cfg, d } = ctx;
  const o = spec.o;
  const radii = TORSO_RINGS[o.torso] ?? TORSO_RINGS.pear;
  const bellyBone = ctx.bi('belly');
  const n = radii.length;
  part(ctx, 'body.torso', (s) => {
    const rings: Ring[] = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const R = torsoRingAt(ctx, t);
      const base = R.skin;
      rings.push({
        c: [0, R.y, 0.004 * t], rs: R.r, rf: R.r * 0.8, skin: base, lw: i === 0 ? 1.3 : 1,
        deform: (p, th) => torsoDeform(p, th, R.bellyAmt, R.hump, o.belly === 'abs'),
        skinAt: R.bellyAmt > 0 ? (th) => {
          const s2 = Math.sin(th * TAU);
          return s2 > 0.3 ? blend(base.bA, bellyBone, Math.min(0.85, (s2 - 0.3) * 1.4)) : base;
        } : undefined,
      });
    }
    loft(s, rings, { sides: cfg.torsoSides, front: [0, 0, 1], stagger: true, mat: ctx.bodyMat, region: REG.torso, capStart: 'flat',
      jitter: 0.025, seed: ctx.seed + 31, tangent: () => [0, 1, 0] });
    if (o.belly === 'ruff') {
      // fluffy ruff: a ring of tufts on the upper chest
      const R = torsoRingAt(ctx, 0.72);
      for (let k = 0; k < 7; k++) {
        const a = (-0.35 + (0.7 * k) / 6) * Math.PI;
        const p: V3 = [Math.sin(a) * R.r, R.y, Math.cos(a) * R.r * 0.8];
        strip(s, [p, add(p, [Math.sin(a) * 0.02, -0.045, Math.cos(a) * 0.02])], [0.016, 0.002], [Math.cos(a), 0, -Math.sin(a)], () => rigid(ctx.bi('spine_02')),
          { mat: ctx.bodyMat, region: REG.torso, lw: 0.8 });
      }
    }
    void d;
  });
}

function arm(ctx: BuildCtx, side: 1 | -1): void {
  const { spec, d, cfg } = ctx;
  const S = side > 0 ? 'L' : 'R';
  const cl = ctx.bi(`clavicle_${S}` as BoneName), ua = ctx.bi(`upperarm_${S}` as BoneName), fa = ctx.bi(`forearm_${S}` as BoneName), hd = ctx.bi(`hand_${S}` as BoneName);
  const pu = ctx.P[`upperarm_${S}` as BoneName]!, pf = ctx.P[`forearm_${S}` as BoneName]!, ph = ctx.P[`hand_${S}` as BoneName]!;
  const wings = spec.flags.wingArms;
  const r = d.armR * (wings ? 0.8 : 1);
  const start: V3 = [side * Math.max(0.035, pu[0] * 0.45), pu[1] + 0.012, pu[2]];
  part(ctx, `body.arm_${S}`, (s) => {
    const pts: V3[] = [start, pu, lerp3(pu, pf, 0.5), pf, lerp3(pf, ph, 0.5), ph];
    const rad = [1.05, 1.0, 0.9, 0.78, 0.7, 0.62];
    const nR = cfg.limbRings + 1;
    const path = nR >= 6 ? pts : [pts[0], pts[1], pts[3], pts[5]];
    const radP = nR >= 6 ? rad : [1.05, 1.0, 0.78, 0.62];
    const rings: Ring[] = path.map((p, i) => ({
      c: p, rs: r * radP[i], rf: r * radP[i] * 0.92, lw: 1,
      skin: i === 0 ? blend(cl, ua, 0.6) : p === pf ? blend(ua, fa, 0.5) : p === ph ? blend(fa, hd, 0.3) : (p[1] > pf[1] ? rigid(ua) : rigid(fa)),
    }));
    loft(s, rings, { sides: cfg.limbSides, front: [0, 0, 1], mat: ctx.bodyMat, region: (seg) => (seg < path.length / 2 - 0.5 ? REG.upperArm : REG.forearm), capStart: 'point',
      capStartLen: r * 0.6, capEnd: 'flat' });
    if (wings) {
      // wing-arms: feather strips hanging from the arm (creatures §B7.3), feather "fingers" at the hand
      const nF = cfg.limbSides >= 6 ? 6 : 4;
      for (let k = 0; k < nF; k++) {
        const t = k / (nF - 1);
        const root = t < 0.5 ? lerp3(pu, pf, t * 2) : lerp3(pf, ph, (t - 0.5) * 2);
        const L = 0.12 + 0.07 * t;
        const dir = nrm([side * 0.25, -1, -0.3 + 0.2 * t]);
        const tip = add(root, scl(dir, L));
        const bone = t < 0.5 ? ua : fa;
        strip(s, [add(root, [side * 0.006, 0, -0.01]), lerp3(root, tip, 0.55), tip], [0.022, 0.018, 0.003], (i) => nrm(cross(dir, [0, 0, 1])).map((x) => x * (i === 0 ? 1 : 1)) as V3,
          () => rigid(bone), { mat: ctx.bodyMat, region: REG.extra, lw: 0.9 });
      }
    }
  });
}

function paw(ctx: BuildCtx, side: 1 | -1): void {
  const { spec, d } = ctx;
  const S = side > 0 ? 'L' : 'R';
  const hd = ctx.bi(`hand_${S}` as BoneName), fa = ctx.bi(`forearm_${S}` as BoneName);
  const ph = ctx.P[`hand_${S}` as BoneName]!;
  const type = spec.o.hands;
  const L = d.pawLen;
  const w = 0.024 * spec.v.handSize * (spec.o.arms === 'beefy' ? 1.25 : 1) * (type === 'human' ? 0.9 : 1);
  const tipDir = nrm([side * 0.05, -1, 0.12]);
  const tip = add(ph, scl(tipDir, L));
  const digits = spec.digits;
  const bare = MAT.flesh;
  part(ctx, `body.paw_${S}`, (s) => {
    if (type === 'hooves') {
      const rs: Ring[] = [0, 0.5, 1].map((t) => ({ c: lerp3(ph, add(ph, scl(tipDir, L * 0.8)), t), rs: w * (0.85 + 0.25 * t), rf: w * (0.85 + 0.2 * t), skin: rigid(hd) }));
      loft(s, rs.slice(0, 2), { sides: 6, front: [0, 0, 1], mat: ctx.bodyMat, region: REG.forearm, capStart: 'none', capEnd: 'none' });
      loft(s, rs.slice(1), { sides: 6, front: [0, 0, 1], mat: MAT.leather, region: REG.forearm, capStart: 'none', capEnd: 'flat', creases: [{ ring: 1, from: 0.2, to: 0.3 }] });
      return;
    }
    if (type === 'talons') {
      for (let k = 0; k < 3; k++) {
        const a = (k - 1) * 0.35;
        const dir = nrm([side * 0.05 + Math.sin(a) * 0.6, -1, 0.3 + Math.cos(a) * 0.1]);
        const pts = resample([ph, add(ph, scl(dir, L * 0.6)), add(ph, add(scl(dir, L), [0, 0, 0.012]))], 3);
        strip(s, pts, [0.006, 0.004, 0.0008], [1, 0, 0], () => rigid(hd), { mat: bare, region: REG.forearm, lw: 1 });
      }
      return;
    }
    // mitten-like palm (all other types), flattened across the palm plane
    const palmL = type === 'human' ? L * 0.55 : L * 0.85;
    const rs: Ring[] = [0, 0.35, 0.75, 1].map((t, i) => ({
      c: add(ph, scl(tipDir, palmL * t)), rs: w * [0.8, 1.05, 1.08, 0.85][i] * (type === 'webbed' ? 1.25 : 1), rf: w * [0.7, 0.62, 0.58, 0.5][i] * (type === 'webbed' ? 0.6 : 1),
      skin: i === 0 ? blend(fa, hd, 0.7) : rigid(hd), lw: 1.1,
    }));
    loft(s, rs, { sides: 5, front: [side * 0.05, 0, 1], mat: ctx.bodyMat, region: REG.forearm, capStart: 'none', capEnd: type === 'human' ? 'flat' : 'point', capEndLen: w * 0.35,
      creases: type === 'human' ? [] : [{ ring: 2, from: 0.15, to: 0.35 }] });
    if (type === 'human') {
      for (let k = 0; k < digits; k++) {
        const x = (k / Math.max(1, digits - 1) - 0.5) * w * 1.5;
        const base = add(add(ph, scl(tipDir, palmL)), [x * side * -1 * 0 + x, 0, 0.002]);
        const dir = nrm(add(tipDir, [0, 0, 0.1]));
        const fl = L * (0.42 + 0.08 * Math.sin((k / Math.max(1, digits - 1)) * Math.PI));
        loft(s, [{ c: base, rs: 0.0055, rf: 0.0045, skin: rigid(hd) }, { c: add(base, scl(dir, fl)), rs: 0.0045, rf: 0.004, skin: rigid(hd) }],
          { sides: 4, front: [0, 0, 1], mat: ctx.bodyMat, region: REG.forearm, capEnd: 'point', capEndLen: 0.004 });
      }
    }
    if (type === 'clawed') {
      for (let k = 0; k < Math.min(3, digits); k++) {
        const x = (k - 1) * w * 0.55;
        const base = add(add(ph, scl(tipDir, palmL * 0.95)), [x, 0, w * 0.35]);
        cone4(s, base, nrm(add(tipDir, [0, 0, 0.6])), 0.003, 0.016, rigid(hd), MAT.ink);
      }
    }
  });
  void tip;
}

function cone4(s: Surface, base: V3, dir: V3, r: number, L: number, skin: Skin, mat: number): void {
  loft(s, [{ c: base, rs: r, rf: r, skin }, { c: add(base, scl(dir, L * 0.4)), rs: r * 0.7, rf: r * 0.7, skin }],
    { sides: 4, front: Math.abs(dir[1]) > 0.9 ? [0, 0, 1] : [0, 1, 0], mat, region: REG.extra, capStart: 'flat', capEnd: 'point', capEndLen: L * 0.6 });
}

function leg(ctx: BuildCtx, side: 1 | -1): void {
  const { spec, d, cfg } = ctx;
  if (spec.flags.slugBody) return;
  const S = side > 0 ? 'L' : 'R';
  const th = ctx.bi(`thigh_${S}` as BoneName), ca = ctx.bi(`calf_${S}` as BoneName), ft = ctx.bi(`foot_${S}` as BoneName);
  const pt = ctx.P[`thigh_${S}` as BoneName]!, pc = ctx.P[`calf_${S}` as BoneName]!, pf = ctx.P[`foot_${S}` as BoneName]!;
  const r = d.legR;
  const birdLeg = spec.o.feet === 'bird';
  const start: V3 = [pt[0] * 0.9, pt[1] + 0.025, pt[2]];
  part(ctx, `body.leg_${S}`, (s) => {
    const full: V3[] = [start, pt, lerp3(pt, pc, 0.5), pc, lerp3(pc, pf, 0.5), pf];
    const rad = birdLeg ? [1.2, 1.1, 0.85, 0.42, 0.36, 0.32] : [1.12, 1.0, 0.9, 0.76, 0.66, 0.56];
    const nR = cfg.limbRings + 1;
    const idx = nR >= 6 ? [0, 1, 2, 3, 4, 5] : [0, 1, 3, 5];
    const rings: Ring[] = idx.map((k) => ({
      c: full[k], rs: r * rad[k], rf: r * rad[k] * 0.95, lw: 1,
      skin: k <= 1 ? rigid(th) : k === 2 ? segSkin(th, ca, pt, pc, full[k]) : k === 3 ? blend(th, ca, 0.5) : k === 4 ? rigid(ca) : blend(ca, ft, 0.5),
    }));
    const kneeIdx = idx.indexOf(3);
    loft(s, rings, { sides: cfg.limbSides, front: [0, 0, 1], mat: birdLeg ? (seg) => (seg >= kneeIdx ? MAT.flesh : ctx.bodyMat) : ctx.bodyMat,
      region: (seg) => (seg < kneeIdx ? REG.thigh : REG.shin), capStart: 'point', capStartLen: r * 0.5, capEnd: 'none' });
  });
}

function foot(ctx: BuildCtx, side: 1 | -1): void {
  const { spec, d } = ctx;
  if (spec.flags.slugBody) return;
  const S = side > 0 ? 'L' : 'R';
  const ft = ctx.bi(`foot_${S}` as BoneName), toe = ctx.bi(`toe_${S}` as BoneName), ca = ctx.bi(`calf_${S}` as BoneName);
  const pf = ctx.P[`foot_${S}` as BoneName]!, pt = ctx.P[`toe_${S}` as BoneName]!;
  const type = spec.o.feet;
  const fs = d.footLen / 0.11;
  const w = 0.026 * (type === 'flat' ? 1.35 : 1) * Math.max(0.85, d.legR / 0.04);
  part(ctx, `body.foot_${S}`, (s) => {
    if (d.digitigrade) {
      // metatarsus from the hock down to the ball of the foot (the leg loft ends at the hock)
      const ball: V3 = [pt[0], 0.016, pt[2] - 0.02 * fs];
      const r0 = d.legR * 0.55;
      loft(s, [{ c: pf, rs: r0, rf: r0, skin: blend(ca, ft, 0.5) }, { c: lerp3(pf, ball, 0.5), rs: r0 * 0.9, rf: r0 * 0.9, skin: rigid(ft) }, { c: ball, rs: r0 * 0.85, rf: r0 * 0.85, skin: blend(ft, toe, 0.5) }],
        { sides: Math.max(4, ctx.cfg.limbSides - 1), front: [0, 0, 1], mat: type === 'bird' ? MAT.flesh : ctx.bodyMat, region: REG.shin, capStart: 'none', capEnd: 'none' });
    }
    if (type === 'bird') {
      const ball: V3 = [pt[0], 0.008, pt[2] - 0.01];
      for (let k = 0; k < 4; k++) {
        const a = k === 3 ? Math.PI : (k - 1) * 0.45;
        const dir = nrm([Math.sin(a) * side * 0.9, 0, Math.cos(a)]);
        const L = (k === 3 ? 0.03 : 0.065) * fs;
        strip(s, [ball, add(ball, add(scl(dir, L), [0, -0.004, 0]))], [0.008, 0.002], [0, 1, 0].map((x) => x) as V3, () => rigid(toe), { mat: MAT.flesh, region: REG.shin, lw: 1, doubleSided: true });
        loft(s, [{ c: ball, rs: 0.006, rf: 0.004, skin: rigid(toe) }, { c: add(ball, scl(dir, L)), rs: 0.003, rf: 0.002, skin: rigid(toe) }],
          { sides: 4, front: [0, 1, 0], mat: MAT.flesh, region: REG.shin, capEnd: 'point', capEndLen: 0.006 });
      }
      return;
    }
    if (type === 'hooves') {
      const c: V3 = [pt[0], 0, pt[2] - 0.02];
      const rs: Ring[] = [0.035, 0.012, 0].map((y, i) => ({ c: [c[0], y, c[2]] as V3, rs: w * [0.85, 1.0, 1.02][i], rf: w * [0.9, 1.1, 1.12][i], skin: i === 0 ? blend(ft, toe, 0.5) : rigid(toe) }));
      loft(s, rs, { sides: 6, front: [0, 0, 1], mat: MAT.leather, region: REG.shin, capStart: 'none', capEnd: 'flat', tangent: () => [0, -1, 0], creases: [{ ring: 1, from: 0.2, to: 0.3 }] });
      return;
    }
    // cat / flat / human feet: a wedge loft heel → toes with a flat sole at y = 0 (bind feet at y = 0)
    const heelZ = d.digitigrade ? pt[2] - 0.045 * fs : pf[2] - 0.022;
    const toeZ = pt[2] + 0.028 * fs * (type === 'flat' ? 1.4 : 1);
    const zs = [heelZ, (heelZ + pt[2]) / 2, pt[2], toeZ];
    const hs = [0.02, 0.024, 0.02, 0.012];
    const ws = [0.75, 0.95, 1.05, 0.9];
    const rings: Ring[] = zs.map((z, i) => ({
      c: [pt[0], hs[i], z] as V3, rs: w * ws[i], rf: hs[i] + 0.002, lw: 1.1,
      skin: i <= 1 ? (d.digitigrade ? rigid(toe) : rigid(ft)) : rigid(toe),
      deform: (p: V3) => [p[0], Math.max(0, p[1]), p[2]] as V3,
    }));
    loft(s, rings, { sides: 6, front: [0, 1, 0], mat: ctx.bodyMat, region: REG.shin, capStart: 'flat', capEnd: 'point', capEndLen: 0.01, tangent: () => [0, 0, 1],
      creases: type === 'human' ? [] : [{ ring: 2, from: 0.15, to: 0.35 }] });
  });
}

function tail(ctx: BuildCtx): void {
  const { spec, d, cfg, rig } = ctx;
  if (!spec.flags.hasTail || rig.chains.tail.length === 0 && spec.o.tail !== 'stub') { if (!spec.flags.hasTail) return; }
  const type = spec.o.tail;
  const bones = rig.chains.tail;
  const pathN = rig.tailPath.map((p) => scl(p, 1 / rig.norm));
  if (pathN.length < 2) return;
  const pelvis = ctx.bi('pelvis');
  const segSkinAt = (u: number): Skin => {
    if (bones.length === 0) return rigid(pelvis);
    const f = u * bones.length;
    const k = Math.min(bones.length - 1, Math.floor(f));
    const frac = f - k;
    if (frac < 0.2 && k > 0) return blend(bones[k - 1], bones[k], 0.5 + frac * 2.5);
    if (k === 0 && frac < 0.15) return blend(pelvis, bones[0], 0.5 + frac * 3.3);
    return rigid(bones[k]);
  };
  const tk = d.tailR / 0.025;
  part(ctx, 'body.tail', (s) => {
    if (type === 'feather') {
      // a fan of tail feathers from the rump (goose, owl)
      const root = pathN[0];
      for (let k = 0; k < 5; k++) {
        const a = (k - 2) * 0.3;
        const off = nrm([Math.sin(a) * 0.9, 0.35, -Math.cos(a)]);
        strip(s, [root, add(root, scl(off, 0.07)), add(root, scl(off, 0.13))], [0.018, 0.02, 0.003], nrm(cross(off, [0, 1, 0])), () => segSkinAt(0.6),
          { mat: ctx.bodyMat, region: REG.tail, lw: 0.8 });
      }
      return;
    }
    if (type === 'pom' || type === 'stub') {
      const root = pathN[0];
      const tipP = pathN[pathN.length - 1];
      if (type === 'pom') {
        const R = 0.035;
        const c = add(root, [0, 0.01, -0.02]);
        const rs: Ring[] = [-0.85, -0.3, 0.3, 0.85].map((t) => ({ c: add(c, [0, 0, -R * t]), rs: R * Math.sqrt(1 - t * t) + 0.002, rf: R * Math.sqrt(1 - t * t) + 0.002, skin: segSkinAt(0.8) }));
        loft(s, rs, { sides: 8, front: [0, 1, 0], mat: ctx.bodyMat, region: REG.tail, capStart: 'point', capEnd: 'point', capStartLen: R * 0.15, capEndLen: R * 0.15, jitter: 0.12, seed: ctx.seed + 41, tangent: () => [0, 0, -1] });
      } else {
        const rs: Ring[] = [{ c: root, rs: 0.03 * tk, rf: 0.028 * tk, skin: rigid(pelvis) }, { c: tipP, rs: 0.022 * tk, rf: 0.02 * tk, skin: segSkinAt(0.9) }];
        loft(s, rs, { sides: cfg.tailSides, front: [0, -1, 0], mat: ctx.bodyMat, region: REG.tail, capEnd: 'point', capEndLen: 0.02 });
      }
      return;
    }
    const n = cfg.tailRings;
    const pts = resample(pathN, n);
    const prof: Record<string, [number, number]> = { question: [1, 0.48], hooked: [1, 0.45], corkscrew: [0.85, 0.5], tufted: [0.8, 0.45], rat: [0.66, 0.16],
      bushy: [1.5, 1.35], dragon: [1, 0.35], forked: [1, 0.5], endless: [0.85, 0.4], feather: [0.7, 0.5] };
    const [r0, r1] = prof[type] ?? [1, 0.5];
    const rings: Ring[] = pts.map((p, i) => {
      const u = i / (n - 1);
      let r = 0.025 * tk * (r0 + (r1 - r0) * u);
      if (type === 'bushy') r *= 0.75 + 0.5 * Math.sin(Math.min(1, u * 1.25) * Math.PI * 0.85);
      return { c: p, rs: r, rf: r, skin: segSkinAt(u), lw: 0.9 - 0.4 * u };
    });
    const mat = type === 'rat' ? MAT.flesh : ctx.bodyMat;
    loft(s, rings, { sides: cfg.tailSides, front: [0, -1, 0], mat, region: REG.tail, capStart: 'flat', capEnd: type === 'tufted' ? 'flat' : 'point',
      capEndLen: type === 'bushy' ? 0.05 : 0.02, jitter: type === 'bushy' ? 0.1 : 0.02, seed: ctx.seed + 43 });
    const last = pts[n - 1], prev = pts[n - 2];
    const dir = nrm(sub(last, prev));
    if (type === 'tufted') {
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * TAU;
        const off = nrm(add(dir, scl(add(scl(nrm(cross(dir, [1, 0, 0])), Math.cos(a)), scl([1, 0, 0], Math.sin(a))), 0.5)));
        strip(s, [last, add(last, scl(off, 0.06))], [0.01, 0.001], nrm(cross(off, [0, 0, 1])), () => segSkinAt(1), { mat: ctx.bodyMat, region: REG.tail, lw: 0.6 });
      }
    }
    if (type === 'feather') {
      for (let k = 0; k < 5; k++) {
        const a = (k - 2) * 0.32;
        const off = nrm([Math.sin(a), 0.25, -Math.cos(a)]);
        strip(s, [pts[1], add(pts[1], scl(off, 0.1)), add(pts[1], scl(off, 0.16))], [0.016, 0.018, 0.002], [Math.cos(a), 0, Math.sin(a)], () => segSkinAt(0.5), { mat: ctx.bodyMat, region: REG.tail, lw: 0.8 });
      }
    }
    if (type === 'dragon') for (let i = 1; i < n - 1; i += 2) cone4(s, add(pts[i], [0, rings[i].rs * 0.8, 0]), [0, 1, 0.2], 0.006, 0.025, segSkinAt(i / (n - 1)), MAT.horn);
    if (type === 'forked') for (const sx of [-1, 1]) cone4(s, last, nrm(add(dir, [sx * 0.6, 0, 0])), 0.008, 0.05, segSkinAt(1), ctx.bodyMat);
  });
}

function shell(ctx: BuildCtx): void {
  const { rig } = ctx;
  if (!ctx.spec.flags.slugBody || rig.chains.shell.length === 0) return;
  const sb = rig.chains.shell[0];
  const piv = ctx.P.shell!;
  part(ctx, 'body.shell', (s) => {
    // log spiral r(θ) = a·e^{bθ}, a disc in the side (YZ) plane (concept: the spiral is seen from the side)
    const a = 0.03, b = 0.22, N = 12, th0 = 0, th1 = 3.5 * Math.PI;
    const R = (t: number): number => a * Math.exp(b * t) * 0.92;
    const outer = R(th1);
    const center: V3 = [piv[0], piv[1] + outer * 0.15, piv[2] - outer * 0.65];
    const pts: V3[] = []; const rad: number[] = [];
    for (let i = 0; i < N; i++) {
      const t = th0 + (th1 - th0) * (i / (N - 1));
      const r = R(t);
      pts.push([center[0], center[1] + Math.sin(t) * r, center[2] + Math.cos(t) * r]);
      rad.push(Math.max(0.006, r * 0.42));
    }
    const rings: Ring[] = pts.map((p, i) => ({ c: p, rs: rad[i], rf: rad[i] * 0.78, skin: rigid(sb), lw: 1 }));
    loft(s, rings, { sides: 6, front: [1, 0, 0], mat: MAT.shell, region: REG.extra, capStart: 'point', capEnd: 'flat', capStartLen: 0.006 });
  });
}

function snailSkirt(ctx: BuildCtx): void {
  const { spec, d } = ctx;
  if (!spec.flags.slugBody) return;
  const pel = ctx.bi('pelvis'), tL = ctx.bi('thigh_L'), tR = ctx.bi('thigh_R');
  const pelvisY = ctx.P.pelvis![1];
  part(ctx, 'body.skirt', (s) => {
    const ys = [0, 0.03, 0.12, 0.26, pelvisY + 0.01];
    const rs = [0.15, 0.14, 0.105, 0.085, 0.072].map((r) => r * Math.max(0.9, d.chestW));
    const rfs = [0.26, 0.22, 0.13, 0.095, 0.075];
    const zs = [-0.03, -0.025, 0, 0.01, 0.005];
    const rings: Ring[] = ys.map((y, i) => ({
      c: [0, y, zs[i]] as V3, rs: rs[i], rf: rfs[i], lw: i === 0 ? 1.3 : 1,
      skin: i >= 3 ? rigid(pel) : rigid(pel),
      skinAt: i < 3 ? (_th, p) => (p[0] > 0 ? blend(pel, tL, 0.5) : blend(pel, tR, 0.5)) : undefined,
      deform: (p: V3) => [p[0], Math.max(0, p[1]), p[2]] as V3,
    }));
    loft(s, rings, { sides: 8, front: [0, 0, 1], mat: ctx.bodyMat, region: REG.thigh, capStart: 'flat', jitter: 0.04, seed: ctx.seed + 51, tangent: () => [0, 1, 0] });
  });
}

export function buildBody(ctx: BuildCtx): void {
  neck(ctx);
  torso(ctx);
  snailSkirt(ctx);
  for (const side of [1, -1] as const) { arm(ctx, side); paw(ctx, side); leg(ctx, side); foot(ctx, side); }
  tail(ctx);
  shell(ctx);
}

export { fan };
